import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { styles } from '../../../styles/themes/style';
import { ISenderIdentity, IUnipileConnectForm, linkedInLoginFields } from '../../../interfaces/models/campaign';
import AddLinkedInAccountModal from '../components/campaigns/AddLinkedInAccountModal';
import { useCampaignsRequest } from '../../../services/apis/campaigns/useCampaignsRequest';
import CampaignEmailSignature from '../components/campaigns/CampaignEmailSignature';
import MailboxSettingsDialog from '../components/campaigns/MailboxSettingsDialog';
import InfoTip from '../components/campaigns/InfoTip';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '../../../interfaces/store/appStore';
import { useAppActions } from '../../../stores/application/useAppActions';
import { useAppHandlers } from '../../../services/actions/app/useAppHandlers';
import { checkpointMode, LINKEDIN_CHECKPOINT_WINDOW_MS } from '../utils/linkedInCheckpoint';
import { announceSendersChanged } from '../utils/senderEvents';
import { formatAccountInMailCreditsLine, formatAccountProducts } from '../utils/inMailCredits';

/**
 * Sending accounts — the workspace-level home for the people a campaign sends as.
 *
 * The same senders are reachable from inside a campaign, but only the ones that
 * campaign uses. Connecting a mailbox or writing a signature is not campaign work:
 * it is done once per person and reused everywhere, and having no page for it meant
 * the only way to reach it was to open some campaign and hope it was the right one.
 */

type Tab = 'accounts' | 'signature';

/**
 * Whether this sender's LinkedIn half works — exactly what the LinkedIn column shows as
 * Connected or Extension.
 *
 * The Connect mailbox link is offered only then. A mailbox is paired with the person it
 * sends as, so with no working LinkedIn account there is either nobody to pair it with
 * (the empty table) or a more urgent fix first — Reconnect LinkedIn, in the row's menu.
 */
function linkedInConnected(i: ISenderIdentity): boolean {
  if (i.linkedin?.kind === 'extension') return true;
  if (!i.linkedin?.accountId) return false;
  return i.linkedin?.status !== 'not_found' && i.linkedin?.status !== 'credentials';
}

/**
 * `inDialog`: mounted inside a campaign's modal. Campaign links then open in a new tab —
 * navigating would leave the modal open over another campaign, or drop unsaved edits.
 */
const SendingAccountsSection: React.FC<{ inDialog?: boolean }> = ({ inDialog = false }) => {
  const {
    getSenderIdentities, setSenderActive, connectWorkspaceLinkedIn, solveWorkspaceLinkedInCheckpoint,
    // The background approval check — see the watcher next to submitCheckpoint.
    checkWorkspaceLinkedInCheckpoint,
    createMailConnectLink, detachMailbox, resumeMailbox, deleteSender,
  } = useCampaignsRequest();

  const { setToast } = useAppActions();
  // The app's own confirmation dialog — window.confirm put up the browser's grey box,
  // titled with the site's address, for the two actions here that cannot be undone.
  const { setDestructiveModal, hideModal } = useAppHandlers();
  const navigate = useNavigate();
  const openCampaign = (campaignId: string) => {
    const url = `/campaigns/${campaignId}/leads`;
    if (inDialog) window.open(url, '_blank', 'noopener');
    else navigate(url);
  };

  const [tab, setTab] = useState<Tab>('accounts');
  const [identities, setIdentities] = useState<ISenderIdentity[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [slots, setSlots] = useState<{ cap: number; used: number; available: number } | null>(null);
  const [showConnect, setShowConnect] = useState(false);
  const [connecting, setConnecting] = useState(false);
  /** A connection stopped on LinkedIn verification. Rendered as its own table row. */
  const [checkpoint, setCheckpoint] = useState<{ accountId: string; type: string; email: string } | null>(null);
  /** Set when the modal is re-authenticating an existing account rather than adding one. */
  const [reconnectName, setReconnectName] = useState<string | null>(null);
  /** The account being reconnected — a cookie login has no email to find it by. */
  const [reconnectAccountId, setReconnectAccountId] = useState<string | null>(null);
  /** Connect failures belong inside the dialog — the page bar renders behind it. */
  const [connectError, setConnectError] = useState('');
  /** Whose mailbox settings are open. */
  const [mailboxFor, setMailboxFor] = useState<ISenderIdentity | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    // 'settings' so this never cancels the campaign's own fetch of the same list —
    // this page opens as a dialog ON TOP of the campaign, with both alive at once.
    const res: any = await getSenderIdentities(true, undefined, 'settings', true);
    setLoading(false);
    const list = res?.data?.identities ?? res?.identities;
    if (Array.isArray(list)) setIdentities(list);
    const s = res?.data?.accountSlots ?? res?.accountSlots;
    if (s) setSlots(s);
    // getSenderIdentities is rebuilt every render by the request hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(); }, [load]);

  /*
   * Read once now, and once more a few seconds later.
   *
   * The LinkedIn status in this table is read live from the provider, and the provider
   * does not update it the instant a sign-in completes. So a row could still say the
   * account was not connected moments after it plainly was — and pressing refresh
   * fixed it, which is exactly the tell.
   *
   * Not a fix for the lag, which is not ours to fix: it just stops the user being the
   * thing that has to notice and reload.
   */
  const settleTimer = useRef<number | null>(null);
  const reloadAndSettle = useCallback(() => {
    // Only ever called after something changed — so the campaign's copy is stale too.
    announceSendersChanged();
    void load();
    if (settleTimer.current) window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => { void load(); }, 5000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  useEffect(() => () => {
    if (settleTimer.current) window.clearTimeout(settleTimer.current);
  }, []);

  /** Fold one changed sender back into the list rather than refetching the page. */
  const merge = useCallback((identity: ISenderIdentity) => {
    setIdentities(prev => prev.map(i => (i._id === identity._id ? { ...i, ...identity } : i)));
  }, []);

  const toggleActive = useCallback(async (identity: ISenderIdentity) => {
    const next = !(identity.active !== false);
    setBusyId(identity._id);
    // Optimistic: the toggle is the feedback, and a spinner on a switch reads as broken.
    merge({ ...identity, active: next });
    const res: any = await setSenderActive(identity._id, next);
    setBusyId('');

    const saved = res?.data?.identity;
    if (saved) { merge(saved); announceSendersChanged(); return; }

    merge({ ...identity, active: !next });          // put it back
    /*
      Say what actually failed. "Could not change that" hid the one case that is
      almost certain during development — the API server still running an older
      build, so this route does not exist yet and the request 404s.
    */
    const status = res?.statusCode ?? res?.status;
    setError(
      status === 404
        ? 'The server does not know this endpoint yet — restart the API and try again.'
        : res?.message || `Could not change that${status ? ` (HTTP ${status})` : ''}.`,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merge]);

  /*
    Unipile's hosted auth. The window is opened synchronously on the click and only
    then pointed at the URL — opening it after the await loses the user gesture and
    Safari blocks it as a popup.
  */
  const connectMailbox = useCallback(async (identity: ISenderIdentity) => {
    setMenuFor(null);
    setError('');
    const popup = window.open('about:blank', 'ld-mail-connect', 'width=520,height=680');
    setBusyId(identity._id);

    const res: any = await createMailConnectLink(identity._id, window.location.href);
    const url = res?.data?.url ?? res?.url;
    if (!url) {
      popup?.close();
      setBusyId('');
      setError(res?.message || 'Could not start the mailbox connection.');
      return;
    }
    if (popup) popup.location.replace(url);

    /*
      What counts as "done" differs between connecting and reconnecting, and getting
      that wrong is what made the reconnect window vanish the moment it appeared.

      A reconnect starts with mail.connected ALREADY true — the mailbox is still
      paired, only its authorisation has lapsed. So a success condition of "connected
      is true" was satisfied by the very first poll, 2.5 seconds in, and the popup was
      closed out from under the user before they had signed in to anything.

      What actually changes on a reconnect is the STATUS going healthy again. So each
      case waits for its own transition, measured against how the mailbox looked when
      the window opened.
    */
    const healthy = (m?: ISenderIdentity['mail']) =>
      !!m?.connected && !m.pausedAt && ['connected', ''].includes(m.status || '');

    const wasHealthy = healthy(identity.mail);
    const isReconnect = !!identity.mail?.connected;

    const started = Date.now();
    const timer = window.setInterval(async () => {
      // The user closed the window, or five minutes passed. Either way stop watching
      // and re-read, rather than leaving the row showing whatever it showed before.
      if (Date.now() - started > 5 * 60 * 1000 || (popup && popup.closed)) {
        window.clearInterval(timer);
        setBusyId('');
        // The pairing may have landed after the last poll — let the campaign look too.
        announceSendersChanged();
        const refreshed: any = await getSenderIdentities(true, undefined, 'settings', true);
        const list: ISenderIdentity[] = refreshed?.data?.identities ?? refreshed?.identities ?? [];
        if (list.length) setIdentities(list);
        const now = list.find(i => i._id === identity._id);
        if (now && !healthy(now.mail)) {
          setToast({
            type: 'error',
            message: `${now.mail?.address || 'The mailbox'} is still not connected. Try again and complete the sign-in in the window that opens.`,
          });
        }
        return;
      }

      const poll: any = await getSenderIdentities(false, undefined, 'settings-poll');
      const list: ISenderIdentity[] = poll?.data?.identities ?? poll?.identities ?? [];
      if (!list.length) return;
      const found = list.find(i => i._id === identity._id);

      // Reconnect: wait for a mailbox that was NOT healthy to become healthy. Fresh
      // connect: wait for one to appear at all.
      const done = isReconnect
        ? (healthy(found?.mail) && !wasHealthy)
        : !!found?.mail?.connected;
      if (!done) return;

      window.clearInterval(timer);
      popup?.close();
      setBusyId('');
      // Still runs when this dialog has already been closed — which is exactly when the
      // campaign behind it most needs telling.
      announceSendersChanged();
      // Re-read live rather than keep the poll's copy: the poll is a cheap non-live
      // read, which neither checks LinkedIn status nor drops unfinished attempts, so
      // painting it would briefly bring back rows the live table leaves out.
      void load();
      setToast({
        type: 'success',
        message: `${found?.mail?.address || 'Mailbox'} is ${isReconnect ? 'reconnected' : 'connected'}.`,
      });
    }, 2500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, setToast]);

  const disconnect = useCallback((identity: ISenderIdentity) => {
    setMenuFor(null);
    setDestructiveModal({
      headingText: 'Disconnect mailbox',
      descriptionText: `Disconnect ${identity.mail?.address || 'this mailbox'}? Email steps for this `
        + 'sender stop until a mailbox is connected again.',
      confirmBtnTitle: 'Disconnect',
      onCancelHandler: () => hideModal(),
      onConfirmHandler: async () => {
        hideModal();
        setBusyId(identity._id);
        await detachMailbox(identity._id);
        setBusyId('');
        announceSendersChanged();
        void load();
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const resume = useCallback(async (identity: ISenderIdentity) => {
    setMenuFor(null);
    setBusyId(identity._id);
    await resumeMailbox(identity._id);
    setBusyId('');
    announceSendersChanged();
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  /*
    Delete the sender outright, which is what frees the LinkedIn seat.

    Switching Active off does not: the account stays connected and keeps occupying its
    seat, so a workspace finished with someone had no way to get the seat back and hit
    the cap while believing it had room.

    Spelled out in the confirm because none of it can be undone — the mailbox pairing,
    the signature and the warm-up history live on this row and go with it. The server
    refuses outright while a live campaign is still sending as this person.
  */
  const removeSender = useCallback((identity: ISenderIdentity) => {
    setMenuFor(null);
    const who = identity.name || identity.linkedin?.name || 'this sender';

    const confirmDelete = async () => {
      hideModal();
      setBusyId(identity._id);
      const res: any = await deleteSender(identity._id);
      setBusyId('');

      if (res?.data?.deleted) {
        setToast({ type: 'success', message: `${who} was deleted.` });
        announceSendersChanged();
        void load();
        return;
      }
      setToast({
        type: 'error',
        message: res?.message || `Could not delete ${who}.`,
      });
    };

    setDestructiveModal({
      headingText: 'Delete sender',
      descriptionText: `Delete ${who}? The LinkedIn account is disconnected, its mailbox is `
        + 'unpaired, and the signature and warm-up history are deleted with it. The seat it '
        + 'uses is freed. This cannot be undone.',
      confirmBtnTitle: 'Delete sender',
      onCancelHandler: () => hideModal(),
      onConfirmHandler: confirmDelete,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, setToast]);

  const connectedCount = useMemo(
    () => identities.filter(i => i.mail?.connected).length,
    [identities],
  );

  /*
    Every way into the LinkedIn dialog goes through here, and it opens clean.

    The error from a refused attempt lived on after it: a second attempt that succeeded
    closed the dialog without clearing it, so the next "Connect account" opened on
    "The provided credentials are invalid." before anything had been typed.
  */
  const openConnect = useCallback((name: string | null, accountId: string | null = null) => {
    setReconnectName(name);
    setReconnectAccountId(accountId);
    setConnectError('');
    setCheckpoint(null);
    setShowConnect(true);
  }, []);

  /*
    Connects the LinkedIn account to the workspace, not to a campaign. Which campaigns
    use it stays their own decision — this page is where the account exists, not where
    it is assigned.
  */
  const submitLinkedIn = useCallback(async (form: IUnipileConnectForm) => {
    setConnecting(true);
    setError('');
    // A retry is a new attempt: the last refusal must not sit under it while it runs,
    // nor survive a success to greet the next opening of the dialog.
    setConnectError('');
    const res: any = await connectWorkspaceLinkedIn({
      ...linkedInLoginFields(form),
      ...(form.country ? { country: form.country } : {}),
      ...(form.method === 'cookies' && reconnectAccountId ? { reconnectAccountId } : {}),
    });
    setConnecting(false);

    /*
      The envelope is { success, message, data }. On a refusal `data` is null, so this
      falls back to the envelope and the server's own reason survives — which is the
      only actionable part when LinkedIn rejects a login.
    */
    const data = res?.data ?? res;
    const who = form.method === 'cookies' ? 'LinkedIn account' : form.liEmail.trim();
    if (data?.status === 'connected') {
      setShowConnect(false);
      setToast({ type: 'success', message: `${who} is connected.` });
      reloadAndSettle();
      return;
    }
    if (data?.status === 'checkpoint') {
      // Step 2 of the same dialog. The modal stays open; only its contents change.
      setCheckpoint({
        accountId: String(data.accountId ?? ''),
        type: String(data.checkpointType ?? ''),
        email: who,
      });
      setConnectError('');
      return;
    }
    setConnectError(data?.reason || res?.message || 'Could not connect that account.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, setToast, reconnectAccountId]);

  /*
    Watch for an in-app approval without waiting for a click — the Settings twin of the
    watcher in CampaignFormPage, and for the same reason: Unipile does not tell anyone
    the phone approval happened unless they ask, and the dialog only asked when
    "Connect account" was pressed. Approving on the phone and looking back at the
    dialog therefore changed nothing until the page was refreshed.

    Checked every few seconds for as long as LinkedIn keeps the attempt alive, and
    paused while a click is already asking.
  */
  const manualCheckRef = useRef(false);

  useEffect(() => {
    if (!checkpoint || checkpointMode(checkpoint.type) !== 'approve') return;
    const cp = checkpoint;
    const startedAt = Date.now();
    let stopped = false;
    let timer: number | undefined;

    const tick = async () => {
      if (stopped) return;
      if (Date.now() - startedAt > LINKEDIN_CHECKPOINT_WINDOW_MS) {
        // Unipile drops the attempt after five minutes; say so instead of going quiet.
        setConnectError('This LinkedIn sign-in expired before it was approved. Go back and connect again.');
        return;
      }
      if (!manualCheckRef.current) {
        const res: any = await checkWorkspaceLinkedInCheckpoint(cp.accountId).catch(() => null);
        if (stopped) return;
        const data = res?.data;
        if (data?.status === 'connected') {
          setCheckpoint(null);
          setShowConnect(false);
          setConnectError('');
          setToast({ type: 'success', message: `${cp.email} is connected.` });
          reloadAndSettle();
          return;
        }
        if (data?.status === 'checkpoint') {
          const next = String(data.checkpointType ?? '');
          if (next && next.toUpperCase() !== cp.type.toUpperCase()) {
            setCheckpoint(c => (c && c.accountId === cp.accountId ? { ...c, type: next } : c));
            return;
          }
        } else if (data?.status === 'failed') {
          // Terminal — expired, or LinkedIn wants a browser check. Say so, stop asking.
          setConnectError(data.reason || 'LinkedIn did not confirm this sign-in.');
          return;
        }
      }
      timer = window.setTimeout(tick, 4000);
    };

    timer = window.setTimeout(tick, 4000);
    return () => { stopped = true; if (timer) window.clearTimeout(timer); };
    // Keyed on the challenge itself — the object is replaced on every type change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkpoint?.accountId, checkpoint?.type]);

  /** Step 2. `code` is absent for in-app approval, which has none to enter. */
  const submitCheckpoint = useCallback(async (code?: string) => {
    if (!checkpoint) return;

    setConnecting(true);
    setConnectError('');
    manualCheckRef.current = true;

    const ask = async (): Promise<any> => {
      const res: any = await solveWorkspaceLinkedInCheckpoint({
        accountId: checkpoint.accountId,
        ...(code ? { code } : {}),
      });
      return { data: res?.data ?? res, envelope: res };
    };

    let { data, envelope } = await ask();

    /*
      An in-app approval is waited for, not re-clicked — same reasoning as the campaign
      form's handleCheckpointSubmit. LinkedIn does not hand the approval to Unipile the
      moment it is tapped, so the first answer is reliably "not yet", and reporting that
      as a failure made an already-approved sign-in look broken.

      Only the no-code path polls: a typed code is right or wrong on submission, and
      re-sending it would be retrying a wrong code.
    */
    if (!code) {
      const deadline = Date.now() + 60_000;
      while (data?.status === 'checkpoint' && Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 3000));
        ({ data, envelope } = await ask());
      }
    }

    manualCheckRef.current = false;
    setConnecting(false);

    if (data?.status === 'connected') {
      const who = checkpoint.email;
      setCheckpoint(null);
      setShowConnect(false);
      setToast({ type: 'success', message: `${who} is connected.` });
      reloadAndSettle();
      return;
    }
    if (data?.status === 'checkpoint') {
      /*
        A different challenge coming back is progress, not a rejection — LinkedIn
        chains an OTP into an in-app approval, and Unipile answers the accepted code
        with the next one. See handleCheckpointSubmit in CampaignFormPage.
      */
      const nextType = String(data.checkpointType ?? '');
      const anotherRound = !!nextType && nextType.toUpperCase() !== checkpoint.type.toUpperCase();

      setCheckpoint(c => (c ? { ...c, type: nextType || c.type } : c));
      if (anotherRound) {
        setConnectError('');
        setToast({ type: 'success', message: 'Code accepted — LinkedIn is asking for one more step.' });
        return;
      }
      setConnectError(code
        ? 'Incorrect code, try again.'
        // Said only after a full minute of asking, so it now means what it says.
        : 'LinkedIn has not confirmed the approval yet. Check the LinkedIn app for a pending request, then try again.');
      return;
    }
    const res = envelope;
    setConnectError(data?.reason || res?.message || 'That verification did not go through.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkpoint, load, setToast]);

  return (
    <Wrap onClick={() => setMenuFor(null)}>
      <Tabs role="tablist">
        <TabBtn type="button" role="tab" $active={tab === 'accounts'} onClick={() => setTab('accounts')}>
          Connected accounts
        </TabBtn>
        <TabBtn type="button" role="tab" $active={tab === 'signature'} onClick={() => setTab('signature')}>
          Email signature
        </TabBtn>
      </Tabs>

      {!!error && <ErrBar>{error}</ErrBar>}

      {tab === 'accounts' && (
        <Card>
          {loading ? (
            <Empty>Loading accounts…</Empty>
          ) : identities.length === 0 ? (
            <Empty>
              <EmptyTitle>No connected accounts</EmptyTitle>
              <EmptyText>Connect the LinkedIn account you want to send messages from.</EmptyText>
            </Empty>
          ) : (
            <>
              <Table>
                <thead>
                  <tr>
                    <Th style={{ width: 80 }}>
                      <ThWithInfo>
                        Active
                        <InfoTip
                          below
                          align="left"
                          text="Switching a sender off keeps it out of NEW campaigns. Campaigns already running keep using it."
                        >
                          <InfoDot aria-label="What Active means">i</InfoDot>
                        </InfoTip>
                      </ThWithInfo>
                    </Th>
                    <Th>Name</Th>
                    <Th>Email</Th>
                    {/* Wide enough for the products + InMail credits line under the status. */}
                    <Th style={{ width: 260 }}>LinkedIn</Th>
                    <Th>Used in a campaign</Th>
                    <Th style={{ width: 60 }} />
                  </tr>
                </thead>
                <tbody>
                  {identities.map(i => {
                    const mail = i.mail;
                    const isActive = i.active !== false;
                    const used = i.campaigns ?? [];
                    return (
                      <tr key={i._id}>
                        <Td>
                          {/* Per row, because the answer differs per row: what THIS
                              switch does when clicked, named for the person it is for. */}
                          <InfoTip
                            align="left"
                            text={isActive
                              ? `Switch off to keep ${i.name || 'this sender'} out of new campaigns.`
                              : `Switch on to offer ${i.name || 'this sender'} when building a new campaign.`}
                          >
                            <Switch
                              type="button"
                              role="switch"
                              aria-checked={isActive}
                              aria-label={`${isActive ? 'Disable' : 'Enable'} ${i.name || 'sender'} for new campaigns`}
                              $on={isActive}
                              disabled={busyId === i._id}
                              onClick={() => toggleActive(i)}
                            >
                              <Knob $on={isActive} />
                            </Switch>
                          </InfoTip>
                        </Td>
                        {/*
                          Name, with the LinkedIn login email under it.

                          The email is the one the account was connected with, from our
                          own mapping — Unipile never returns it. Without it a sender whose
                          name had not come back from LinkedIn read "Unnamed sender" with
                          nothing else to go on. When there is no name at all, the email
                          stands in for it rather than a placeholder that looks like a bug.
                        */}
                        <Td>
                          <Strong>{i.name || i.linkedin?.name || i.linkedin?.email || 'LinkedIn account'}</Strong>
                          {!!i.linkedin?.email && !!(i.name || i.linkedin?.name) && (
                            <SubLine>{i.linkedin.email}</SubLine>
                          )}
                        </Td>
                        <Td>
                          {mail?.connected
                            ? <span>{mail.address}</span>
                            // The fix where the gap is, rather than only in the ⋯ menu —
                            // but only once LinkedIn works; see linkedInConnected.
                            : linkedInConnected(i)
                              ? (
                                <RowLink
                                  type="button"
                                  disabled={busyId === i._id}
                                  onClick={() => connectMailbox(i)}
                                >
                                  {busyId === i._id ? 'Connecting…' : 'Connect mailbox'}
                                </RowLink>
                              )
                              : <Pill $tone="bad">Not connected</Pill>}
                        </Td>
                        {/*
                          An extension sender has no connected LinkedIn account at all —
                          steps run through the user's own logged-in browser. Calling
                          that "Connected" is why this table showed three connected
                          accounts while the campaign's account picker correctly said
                          the workspace has none: they are two different things.
                        */}
                        <Td>
                          {i.linkedin?.kind === 'extension'
                            ? <Pill $tone="info" title="Sends through your own logged-in LinkedIn, via the browser extension">Extension</Pill>
                            : !i.linkedin?.accountId
                              ? <Pill $tone="bad">Not connected</Pill>
                              // "Gone" was jargon for a state the user reads as simply
                              // not connected — and the fix is the same either way, so
                              // the distinction only ever confused. It survives in the
                              // tooltip, where it explains rather than labels.
                              : i.linkedin?.status === 'not_found'
                                ? <Pill $tone="bad" title="This account no longer exists at the provider. Use Reconnect LinkedIn to connect it again.">Not connected</Pill>
                                : i.linkedin?.status === 'credentials'
                                  ? <Pill $tone="warn" title="LinkedIn is asking for the credentials again">Needs reconnect</Pill>
                                  : <Pill $tone="good">Connected</Pill>}
                          {/*
                            TODO: nicer presentation of products + InMail credits (badges per
                            product instead of one text line). Also verify the Recruiter credit
                            number — Unipile returned the same 668219 for two different accounts.
                          */}
                          {(() => {
                            const line = [
                              formatAccountProducts(i.linkedin?.premiumFeatures),
                              formatAccountInMailCreditsLine(i.linkedin?.inMailCredits),
                            ].filter(Boolean).join(' · ');
                            return line ? <SubLine title={line}>{line}</SubLine> : null;
                          })()}
                        </Td>
                        <Td>
                          {/* Only active / paused campaigns are listed — those open on their leads page. */}
                          {used.length === 0
                            ? <Muted>Not used</Muted>
                            : used.length === 1
                              ? (
                                <RowLink type="button" onClick={() => openCampaign(used[0]._id)}>
                                  <CampaignStatusDot $status={used[0].status} title={campaignStatusLabel(used[0].status)} />
                                  {used[0].name}
                                </RowLink>
                              )
                              : (
                                <MenuWrap onClick={e => e.stopPropagation()}>
                                  <RowLink
                                    type="button"
                                    title={used.map(c => c.name).join(', ')}
                                    onClick={() => setMenuFor(menuFor === `campaigns:${i._id}` ? null : `campaigns:${i._id}`)}
                                  >
                                    {used.length} campaigns ▾
                                  </RowLink>
                                  {menuFor === `campaigns:${i._id}` && (
                                    <CampaignsMenu>
                                      {used.map(c => (
                                        <MenuItem
                                          key={c._id}
                                          type="button"
                                          onClick={() => { setMenuFor(null); openCampaign(c._id); }}
                                        >
                                          <CampaignStatusDot $status={c.status} />
                                          {c.name}
                                          <CampaignStatusText>{campaignStatusLabel(c.status)}</CampaignStatusText>
                                        </MenuItem>
                                      ))}
                                    </CampaignsMenu>
                                  )}
                                </MenuWrap>
                              )}
                        </Td>
                        <Td>
                          <MenuWrap onClick={e => e.stopPropagation()}>
                            <DotsBtn
                              type="button"
                              aria-label="Actions"
                              onClick={() => setMenuFor(menuFor === i._id ? null : i._id)}
                            >
                              ⋯
                            </DotsBtn>
                            {menuFor === i._id && (
                              <Menu>
                                {/* LinkedIn first — an account that cannot sign in makes
                                    the mailbox beside it moot. */}
                                {i.linkedin?.kind === 'unipile'
                                  && (i.linkedin?.status === 'credentials' || i.linkedin?.status === 'not_found') && (
                                  <MenuItem
                                    type="button"
                                    onClick={() => {
                                      setMenuFor(null);
                                      openConnect(i.name || i.linkedin?.name || null, i.linkedin?.accountId || null);
                                    }}
                                  >
                                    Reconnect LinkedIn
                                  </MenuItem>
                                )}
                                {!mail?.connected && (
                                  <MenuItem type="button" onClick={() => connectMailbox(i)}>
                                    Connect mailbox
                                  </MenuItem>
                                )}
                                {/* Deliverability and the daily cap live here rather
                                    than on the campaign's sender card: both belong to
                                    the mailbox and apply to every campaign it sends
                                    from. */}
                                {mail?.connected && (
                                  <MenuItem
                                    type="button"
                                    onClick={() => { setMenuFor(null); setMailboxFor(i); }}
                                  >
                                    Daily sending limit
                                    {(i.mail?.domainCheck?.warnings?.length ?? 0) > 0 && <Dot aria-hidden />}
                                  </MenuItem>
                                )}
                                {mail?.connected && (
                                  <MenuItem type="button" onClick={() => connectMailbox(i)}>
                                    Reconnect mailbox
                                  </MenuItem>
                                )}
                                {!!mail?.pausedAt && (
                                  <MenuItem type="button" onClick={() => resume(i)}>
                                    Resume sending
                                  </MenuItem>
                                )}
                                {mail?.connected && (
                                  <MenuItem type="button" $danger onClick={() => disconnect(i)}>
                                    Disconnect mailbox
                                  </MenuItem>
                                )}
                                {/* Last, and separated: everything above is reversible,
                                    this is not — and it is the only one that gives the
                                    seat back. */}
                                <MenuSep />
                                <MenuItem type="button" $danger onClick={() => removeSender(i)}>
                                  Delete sender
                                </MenuItem>
                              </Menu>
                            )}
                          </MenuWrap>
                        </Td>
                      </tr>
                    );
                  })}

                </tbody>
              </Table>

              {/* What Active does is on the column header now. All that is left worth
                  saying is the one number the table does not total up itself. */}
              <CardFoot>
                {connectedCount} of {identities.length} {identities.length === 1 ? 'sender has' : 'senders have'} a mailbox connected.
              </CardFoot>
            </>
          )}


          {/*
            Connects a LINKEDIN account. Rendered whether or not there are accounts yet —
            with none, this is the only thing on the page worth doing, and the empty
            state used to send people off to a campaign to do it.

            A mailbox is attached to an account that already exists, so it is offered in
            that account's row — its Email cell and its ⋯ menu — never here, where it
            could not say whose mailbox it would be.
          */}
          {!loading && (
            <ConnectRow>
              <ConnectBtn type="button" onClick={() => openConnect(null)}>
                <Plus aria-hidden>+</Plus> Connect account
              </ConnectBtn>
              {slots && (
                <Slots>
                  ({slots.available} available {slots.available === 1 ? 'slot' : 'slots'})
                </Slots>
              )}
            </ConnectRow>
          )}
        </Card>
      )}

      {tab === 'signature' && (
        <Card>
          {loading ? (
            <Empty>Loading signatures…</Empty>
          ) : identities.length === 0 ? (
            /*
              Nobody to write a signature for yet.

              A signature belongs to a sender, and the editor renders nothing without
              one — so a workspace with no senders got an empty card on this tab, with
              no word about why or what to do. The same states as the accounts tab
              instead: loading, an explanation, and the one action that fixes it.
            */
            <Empty>
              <EmptyTitle>No senders yet</EmptyTitle>
              <EmptyText>
                A signature belongs to a sender. Connect the LinkedIn account you send
                as, then write its signature here — it is available in every campaign
                that sender is used in.
              </EmptyText>
              <EmptyAction>
                <ConnectBtn type="button" onClick={() => openConnect(null)}>
                  <Plus aria-hidden>+</Plus> Connect account
                </ConnectBtn>
              </EmptyAction>
            </Empty>
          ) : (
            /* The editor explains itself in its own info box, under the field. */
            <CampaignEmailSignature
              identities={identities}
              // The campaign's composer offers {{signature}} only when a sender has one.
              onSaved={saved => { merge(saved); announceSendersChanged(); }}
            />
          )}
        </Card>
      )}

      {/* The same modal the campaign form uses — country, LinkedIn email, password.
          One form for one thing, wherever it is opened from. */}
      {/* Reconnect goes through the same submit: connectAccount recognises the login
          email from the workspace mapping and re-authenticates that account in place
          rather than creating a second one. */}
      <MailboxSettingsDialog
        show={!!mailboxFor}
        identity={mailboxFor}
        onHide={() => setMailboxFor(null)}
        onSaved={saved => { merge(saved); setMailboxFor(saved); }}
      />

      {/* Credentials, then verification, then a toast — one dialog throughout, so the
          flow never hands the user back to a page to find the next step. */}
      <AddLinkedInAccountModal
        show={showConnect}
        mode={reconnectName ? 'reconnect' : 'add'}
        accountName={reconnectName}
        submitting={connecting}
        error={connectError}
        checkpointType={checkpoint?.type ?? null}
        onVerify={submitCheckpoint}
        onBack={() => { setCheckpoint(null); setConnectError(''); }}
        onBuySeat={() => { setShowConnect(false); navigate(AppRoutes.SETTINGS_BILLING); }}
        onHide={() => {
          setShowConnect(false);
          setReconnectName(null);
          setReconnectAccountId(null);
          setConnectError('');
          // Drop the pending checkpoint too, or reopening would land straight on step 2.
          setCheckpoint(null);
        }}
        onSubmit={submitLinkedIn}
      />
    </Wrap>
  );
};

export default SendingAccountsSection;

// ─── Styles ───────────────────────────────────────────────────────────────────

const ACCENT = styles.colors.primary600;

const Wrap = styled.div`
  display: flex; flex-direction: column; gap: 16px;
  padding: 22px 26px; width: 100%; box-sizing: border-box;
`;

const Tabs = styled.div`
  display: flex; align-items: center; gap: 22px;
  border-bottom: 1px solid #e6eaf1;
`;

const TabBtn = styled.button<{ $active?: boolean }>`
  border: none; background: none; cursor: pointer;
  padding: 10px 2px; margin-bottom: -1px;
  border-bottom: 2px solid ${p => (p.$active ? ACCENT : 'transparent')};
  font-family: NunitoSans; font-size: 13px; font-weight: 700;
  color: ${p => (p.$active ? ACCENT : '#64748b')};
  &:hover { color: ${ACCENT}; }
`;

const Card = styled.div`
  display: flex; flex-direction: column; gap: 14px;
  background: #fff; border: 1px solid #e6eaf1; border-radius: 12px;
  padding: 18px 20px;
`;


const Table = styled.table`
  width: 100%; border-collapse: collapse;
`;

const Th = styled.th<{ $center?: boolean }>`
  text-align: ${p => (p.$center ? 'center' : 'left')}; padding: 8px 10px;
  border-bottom: 1px solid #eef1f6;
  font-family: NunitoSans; font-size: 10px; font-weight: 800;
  text-transform: uppercase; letter-spacing: 0.06em; color: #94a3b8;
  white-space: nowrap;
`;

/* `vertical-align: middle` matters: an inline-flex box is baseline-aligned in a table
   cell, and the dot is taller than the 10px label, so the baseline put the whole group
   low. Centring the box in the cell instead lines the two up. */
const ThWithInfo = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  vertical-align: middle;
`;

const InfoDot = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 14px; height: 14px; border-radius: 50%;
  border: 1px solid #cbd5e1; color: #94a3b8;
  /* line-height 1 so the glyph sits centred rather than on its own baseline. */
  font-size: 9px; font-weight: 800; font-style: italic; line-height: 1; cursor: help;
  text-transform: none; letter-spacing: 0;
`;
const Td = styled.td<{ $center?: boolean }>`
  padding: 12px 10px;
  border-bottom: 1px solid #f2f5f9;
  font-family: NunitoSans; font-size: 13px; color: #334155;
  vertical-align: middle;
  ${p => (p.$center ? 'text-align: center;' : '')}
`;

const Strong = styled.span`font-weight: 700; color: #0f172a;`;

/* The LinkedIn login email, under the sender's name. */
const SubLine = styled.div`
  margin-top: 2px;
  font-family: NunitoSans; font-size: 11.5px; color: #94a3b8;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;

const Muted = styled.span`color: #94a3b8;`;

const PILL_TONES = {
  good: { bg: '#ecfdf5', fg: '#047857' },
  bad:  { bg: '#fef2f2', fg: '#b91c1c' },
  info: { bg: '#eef2ff', fg: styles.colors.primary600 },
  warn: { bg: '#fffbeb', fg: '#b45309' },
} as const;

/*
 * The negative left margin cancels the pill's own left padding, so the TEXT inside it
 * starts on the column's edge rather than 8px in. Without it a pill sat one notch right
 * of its header, and — in the Email column, which shows a bare address when a mailbox
 * IS connected — right of the plain text in the rows above and below it. The tinted
 * background bleeds into the cell's 10px padding, which is empty space anyway.
 *
 * `$centered` drops the offset, for a column whose content is centred: there the pill
 * lines up by its middle, and the offset would only push it 4px off.
 */
const Pill = styled.span<{ $tone: keyof typeof PILL_TONES; $centered?: boolean }>`
  display: inline-block; padding: 2px 8px; border-radius: 999px;
  margin-left: ${p => (p.$centered ? '0' : '-8px')};
  font-size: 11px; font-weight: 700;
  background: ${p => PILL_TONES[p.$tone].bg};
  color: ${p => PILL_TONES[p.$tone].fg};
`;

const Switch = styled.button<{ $on: boolean }>`
  position: relative;
  width: 34px; height: 19px; border-radius: 999px; border: none; cursor: pointer;
  padding: 0;
  background: ${p => (p.$on ? ACCENT : '#cbd5e1')};
  transition: background 0.15s;
  &:disabled { opacity: .6; cursor: default; }
`;

const Knob = styled.span<{ $on: boolean }>`
  position: absolute; top: 2px; left: ${p => (p.$on ? '17px' : '2px')};
  width: 15px; height: 15px; border-radius: 50%; background: #fff;
  transition: left 0.15s;
`;

const MenuWrap = styled.div`position: relative;`;

const DotsBtn = styled.button`
  border: none; background: none; cursor: pointer;
  padding: 2px 8px; border-radius: 6px;
  font-size: 16px; line-height: 1; color: #64748b;
  &:hover { background: #f1f5f9; color: ${ACCENT}; }
`;

const Menu = styled.div`
  position: absolute; right: 0; top: calc(100% + 4px); z-index: 40;
  min-width: 180px; padding: 5px;
  background: #fff; border: 1px solid #e2e8f0; border-radius: 10px;
  box-shadow: 0 8px 24px rgba(20,30,80,0.12);
  display: flex; flex-direction: column;
`;

const MenuSep = styled.div`
  height: 1px; margin: 4px 2px; background: #eef1f6;
`;

/** Campaign list under "N campaigns" — opens left-aligned under its column. */
const CampaignsMenu = styled(Menu)`left: 0; right: auto; max-height: 260px; overflow-y: auto;`;

/** The listing only returns active / paused campaigns. */
const campaignStatusLabel = (status: string) => (status === 'paused' ? 'Paused' : 'Active');

const CampaignStatusDot = styled.span<{ $status: string }>`
  display: inline-block; width: 7px; height: 7px; border-radius: 50%;
  margin-right: 6px; vertical-align: middle;
  background: ${p => (p.$status === 'paused' ? PILL_TONES.warn.fg : PILL_TONES.good.fg)};
`;

const CampaignStatusText = styled.span`
  margin-left: 8px; font-size: 11px; font-weight: 600; color: #94a3b8;
`;

const MenuItem = styled.button<{ $danger?: boolean }>`
  border: none; background: none; cursor: pointer; text-align: left;
  padding: 8px 10px; border-radius: 7px;
  font-family: NunitoSans; font-size: 13px;
  color: ${p => (p.$danger ? '#b91c1c' : '#334155')};
  &:hover { background: ${p => (p.$danger ? '#fef2f2' : '#f6f8fc')}; }
`;

/* Same weight as "Connect mailbox" in the campaign's account picker. */
const RowLink = styled.button`
  border: none; background: none; cursor: pointer; padding: 0;
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: ${ACCENT};
  &:hover:not(:disabled) { text-decoration: underline; }
  &:disabled { color: #94a3b8; cursor: default; }
`;

/* Centred under the table, as a plain link — the dashed block read as a drop target. */
const ConnectRow = styled.div`
  display: flex; align-items: baseline; justify-content: center; gap: 6px;
  padding: 14px 0 4px;
`;

const ConnectBtn = styled.button`
  display: inline-flex; align-items: baseline; gap: 5px;
  border: none; background: none; cursor: pointer; padding: 4px 8px;
  border-radius: 8px;
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: ${ACCENT};
  /* No underline: it ran under the plus too, which made the glyph look struck out. */
  &:hover { background: ${styles.colors.primary100}; }
`;

/* Its own size — at the label's 13px the plus reads as a stray character rather
   than the "add" affordance it is. */
const Plus = styled.span`
  font-size: 18px; font-weight: 600; line-height: 1;
`;

const Slots = styled.span`
  font-family: NunitoSans; font-size: 11px; color: #94a3b8;
`;








const Dot = styled.span`
  display: inline-block; width: 6px; height: 6px; border-radius: 50%;
  background: #f59e0b; margin-left: 6px; vertical-align: middle;
`;

const CardFoot = styled.div`
  font-family: NunitoSans; font-size: 11px; line-height: 1.5; color: #94a3b8;
`;

const Empty = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  padding: 34px 8px; text-align: center;
  font-family: NunitoSans; font-size: 13px; color: #94a3b8;
`;

const EmptyTitle = styled.div`
  font-size: 14px; font-weight: 800; color: #475569;
`;

const EmptyText = styled.div`
  max-width: 440px; margin: 0 auto; line-height: 1.6;
`;

const EmptyAction = styled.div`
  display: flex; justify-content: center; margin-top: 6px;
`;

const ErrBar = styled.div`
  border: 1px solid #fecaca; background: #fef2f2; border-radius: 8px;
  padding: 9px 12px;
  font-family: NunitoSans; font-size: 12px; color: #b91c1c;
`;
